import { Button } from 'primereact/button';
import { Trans, useTranslation } from 'react-i18next';

import canale5LogoImage from '@features/campaigns/builder/assets/images/canale-5-logo.svg';
import tvFrameImage from '@features/campaigns/builder/assets/images/creative-preview-tv-frame-opt.png';
import tvProgramBorderGuardImage from '@features/campaigns/builder/assets/images/tv-program-border-guard.png';
import tvProgramPlaceholderImageES from '@features/campaigns/builder/assets/images/tv-program-es-placeholder.jpg';
import tvProgramPlaceholderImageIT from '@features/campaigns/builder/assets/images/tv-program-it-placeholder.jpg';
import { PreviewCreativeModalOptions } from '@features/campaigns/builder/components/FormFields/FieldTypes.ts';
import { useCampaignCountry } from '@features/campaigns/shared/providers/CampaignCountryProvider/useCampaignCountry.ts';
import { InfoPanel } from '@shared/components/InfoPanel/InfoPanel.tsx';
import { InfoPanelTypes } from '@shared/components/InfoPanel/InfoPanel.types.ts';
import { LinkText } from '@shared/components/LinkText/LinkText';
import { i18nNameSpace } from '@shared/consts/i18n.ts';
import { UserCountryCode, UserCountryCodes } from '@shared/types/countryCodes.ts';

import styles from './PreviewTVWrapper.module.scss';

interface PreviewTVWrapperProps {
  imageUrl: string;
  editablePreview?: boolean;
  options?: PreviewCreativeModalOptions;
}

interface PreviewDataPerCountry {
  lannerInstructionsUrl: string;
  placeholderImageUrl: string;
  translationKeyContentHint01: string;
  hint02LannerTextValue: string;
}

const LANNER_INSTRUCTIONS_IT_URL = 'https://admanager.mediaset.it/it/lanner/#EI4Rfl';
const LANNER_INSTRUCTIONS_ES_URL = 'https://admanager.mediaset.es/es/lanner/';

function determinePreviewDataPerCountry(country: UserCountryCode): PreviewDataPerCountry {
  switch (country) {
    case UserCountryCodes.ES: {
      return {
        placeholderImageUrl: tvProgramPlaceholderImageES,
        lannerInstructionsUrl: LANNER_INSTRUCTIONS_ES_URL,
        translationKeyContentHint01: 'builder.lShapePreview.content.hint01ES',
        hint02LannerTextValue: 'Publicidad Digital',
      };
    }
    case UserCountryCodes.IT: {
      return {
        placeholderImageUrl: tvProgramPlaceholderImageIT,
        lannerInstructionsUrl: LANNER_INSTRUCTIONS_IT_URL,
        translationKeyContentHint01: 'builder.lShapePreview.content.hint01',
        hint02LannerTextValue: 'Pubblicità',
      };
    }
    default: {
      return {
        placeholderImageUrl: tvProgramPlaceholderImageIT,
        lannerInstructionsUrl: LANNER_INSTRUCTIONS_IT_URL,
        translationKeyContentHint01: 'builder.lShapePreview.content.hint01',
        hint02LannerTextValue: 'Pubblicità',
      };
    }
  }
}

export function PreviewTVWrapper({ imageUrl, editablePreview, options }: PreviewTVWrapperProps) {
  const { countryCode } = useCampaignCountry();
  const { t } = useTranslation([i18nNameSpace.CAMPAIGNS]);

  const { placeholderImageUrl, lannerInstructionsUrl, translationKeyContentHint01, hint02LannerTextValue } =
    determinePreviewDataPerCountry(countryCode);

  return (
    <div className={styles.container}>
      {editablePreview && (
        <div className={styles.contentContainer}>
          <InfoPanel type={InfoPanelTypes.INFO} headerText={t('builder.lShapePreview.header')}>
            <ul>
              <li key={'hint01'}>
                <Trans
                  t={t}
                  i18nKey={translationKeyContentHint01}
                  components={[<LinkText externalLink key={0} to={lannerInstructionsUrl} />]}
                />
              </li>
              <li key={'hint02'}>
                {t('builder.lShapePreview.content.hint02', { requiredLannerText: hint02LannerTextValue })}
              </li>
              <li key={'hint03'}>{t('builder.lShapePreview.content.hint03')}</li>
              <li key={'hint04'}>{t('builder.lShapePreview.content.hint04')}</li>
            </ul>
          </InfoPanel>
        </div>
      )}

      <div className={styles.tvContainer}>
        <img src={tvFrameImage} alt={'tv-frame'} className={styles.tvFrameBackground} />
        <div className={styles.creativeCheckeredBg}>
          <img
            src={tvProgramBorderGuardImage}
            alt={'tv-program-broadcast-border-guard'}
            className={styles.broadcastBorder}
          />
          <img src={placeholderImageUrl} alt={'tv-program-placeholder'} className={styles.tvProgramPlaceholder} />
          <div className={styles.creativePreviewBox}>
            <img src={imageUrl} alt={'creative-preview'} className={styles.creativeImage} />
          </div>
        </div>

        <img src={canale5LogoImage} alt={'canale-5-logo'} className={styles.canale5Logo} />
      </div>

      {editablePreview && (
        <div className={styles.footer}>
          <Button
            data-testid='tv-preview-reject-cta-button'
            className={styles.editButton}
            icon='pi pi-trash'
            onClick={options?.rejectImageCallback}
            text
            severity='danger'
            type='button'
          >
            {t('builder.modals.deleteImage')}
          </Button>
          <Button
            data-testid='tv-preview-accept-cta-button'
            className={styles.deleteButton}
            onClick={options?.acceptImageCallback}
            type='button'
          >
            {t('builder.modals.ok')}
          </Button>
        </div>
      )}
    </div>
  );
}
